import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { benachrichtigeRechteAenderung } from '../../../hooks/useRechte';
import { diffMatrix, gruppiereSchluessel, matrixZeilen } from '../../../lib/rechteMatrix';
import {
  AKTIONEN, API_URL, STIL, aktionLabel, fehlerText, modulLabel, schluesselLabel, werteFuer, wertLabel,
} from './rechteUtil';

interface Zelle {
  wert: string;
  default: string;
  ueberschrieben: boolean;
}
type Rollen = Record<string, Record<string, Zelle>>;

/** Tab "Rollen": Matrix Modul x Aktion je Rolle plus Fähigkeiten und Agenten. */
const RechteMatrix: React.FC = () => {
  const [rollen, setRollen] = useState<Rollen>({});
  const [auswahl, setAuswahl] = useState<string>('');
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  const laden = useCallback(() => {
    return axios.get<Rollen>(`${API_URL}/api/rechte/rollen`)
      .then(r => {
        setRollen(r.data);
        setAuswahl(a => (a && (a === 'Admin' || r.data[a]) ? a : Object.keys(r.data)[0] ?? ''));
      })
      .catch(err => setFehler(fehlerText(err, 'Rechte konnten nicht geladen werden.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const zellen = useMemo(() => rollen[auswahl] ?? {}, [rollen, auswahl]);
  const alt = useMemo(() => {
    const m: Record<string, string> = {};
    Object.entries(zellen).forEach(([k, z]) => { m[k] = z.wert; });
    return m;
  }, [zellen]);

  // Entwurf bei Rollenwechsel bzw. nach dem Neuladen auf den Serverstand setzen
  useEffect(() => { setEntwurf(alt); }, [alt]);

  const gruppen = useMemo(() => gruppiereSchluessel(Object.keys(zellen)), [zellen]);
  const zeilen = useMemo(() => matrixZeilen(gruppen.module, AKTIONEN), [gruppen.module]);
  const aenderungen = diffMatrix(alt, entwurf);
  const anzahlAenderungen = Object.keys(aenderungen).length;
  // Admin hat immer alle Rechte und ist nicht veränderbar
  const schreibgeschuetzt = auswahl === 'Admin';
  const rollenNamen = useMemo(
    () => (rollen.Admin ? Object.keys(rollen) : [...Object.keys(rollen), 'Admin']),
    [rollen],
  );

  const waehleRolle = (name: string) => {
    setAuswahl(name);
    setFehler(null);
    setErfolg(null);
  };

  const speichern = async () => {
    setSaving(true);
    setFehler(null);
    setErfolg(null);
    try {
      await axios.put(`${API_URL}/api/rechte/rollen/${encodeURIComponent(auswahl)}`, aenderungen);
      benachrichtigeRechteAenderung();
      await laden();
      setErfolg('Gespeichert.');
    } catch (err) {
      setFehler(fehlerText(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  const zuruecksetzen = async () => {
    if (!window.confirm(`Alle Rechte der Rolle "${auswahl}" auf den Standard zurücksetzen?`)) return;
    setSaving(true);
    setFehler(null);
    setErfolg(null);
    try {
      await axios.delete(`${API_URL}/api/rechte/rollen/${encodeURIComponent(auswahl)}`);
      benachrichtigeRechteAenderung();
      await laden();
      setErfolg('Auf Standard zurückgesetzt.');
    } catch (err) {
      setFehler(fehlerText(err, 'Zurücksetzen fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  const zelle = (schluessel: string) => {
    const z = zellen[schluessel];
    if (!z) return <span style={{ color: 'var(--ink-2)' }}>—</span>;
    const wert = entwurf[schluessel] ?? z.wert;
    const geaendert = wert !== z.wert;
    return (
      <span title={z.ueberschrieben ? `Standard: ${wertLabel(z.default)}` : undefined} style={{ whiteSpace: 'nowrap' }}>
        <select
          value={wert}
          disabled={saving}
          onChange={e => setEntwurf(d => ({ ...d, [schluessel]: e.target.value }))}
          style={{ ...STIL.select, ...(geaendert ? { borderColor: 'var(--gold)' } : {}) }}
          aria-label={schluesselLabel(schluessel)}
        >
          {werteFuer(schluessel).map(w => <option key={w} value={w}>{wertLabel(w)}</option>)}
        </select>
        {z.ueberschrieben && <span style={STIL.badge}>geändert</span>}
      </span>
    );
  };

  const liste = (titel: string, keys: string[]) => keys.length === 0 ? null : (
    <>
      <div style={STIL.untertitel}>{titel}</div>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {keys.map(k => (
            <tr key={k}>
              <td style={{ ...STIL.td, paddingLeft: 0, minWidth: 220 }}>{schluesselLabel(k)}</td>
              <td style={STIL.td}>{zelle(k)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  if (loading) return <div>Lade Rechte…</div>;

  return (
    <div>
      {fehler && <div style={STIL.fehler}>{fehler}</div>}
      {erfolg && <div style={{ ...STIL.hinweis, color: 'var(--green-500)' }}>{erfolg}</div>}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {rollenNamen.map(r => (
          <button
            key={r}
            type="button"
            className={`k-btn k-btn--sm ${auswahl === r ? 'k-btn--primary' : 'k-btn--ghost'}`}
            onClick={() => waehleRolle(r)}
          >
            {r}
          </button>
        ))}
      </div>

      {schreibgeschuetzt ? (
        <div style={STIL.hinweis}>
          Die Rolle Admin hat immer alle Rechte und kann nicht eingeschränkt werden.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={STIL.th}>Modul</th>
                  {AKTIONEN.map(a => <th key={a} style={STIL.th}>{aktionLabel(a)}</th>)}
                </tr>
              </thead>
              <tbody>
                {gruppen.module.map(modul => (
                  <tr key={modul}>
                    <td style={STIL.td}>{modulLabel(modul)}</td>
                    {zeilen.filter(z => z.modul === modul).map(z => (
                      <td key={z.schluessel} style={STIL.td}>{zelle(z.schluessel)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {liste('Fähigkeiten', gruppen.faehigkeiten)}
          {liste('Agenten im Chat', gruppen.agenten)}

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <button
              type="button"
              className="k-btn k-btn--primary"
              disabled={saving || anzahlAenderungen === 0}
              onClick={speichern}
            >
              {saving ? 'Speichert…' : `Speichern${anzahlAenderungen ? ` (${anzahlAenderungen})` : ''}`}
            </button>
            <button type="button" className="k-btn k-btn--ghost" disabled={saving} onClick={zuruecksetzen}>
              Auf Standard zurücksetzen
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RechteMatrix;
